import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { API_REQUEST_TIMEOUT_MS, authRequestErrorMessage } from "../auth/authRequest";
import { toast } from "sonner";
import { CheckCircle2, MapPin } from "lucide-react";
import { formatUsd } from "../lib/currencyDisplay";
import { formatTripDateLabel, formatTripTimeLabel } from "../lib/dateLocal";

export default function ParkingComplete() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const sessionId = params.get("session_id");
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);

  const confirmPayment = useCallback(async () => {
    if (!sessionId) {
      setError("Missing payment session");
      return;
    }
    setCompleting(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/payments/stripe/complete`,
        { session_id: sessionId },
        { withCredentials: true, timeout: API_REQUEST_TIMEOUT_MS },
      );
      setConfirmed(res.data);
      toast.success("Payment confirmed — parking reserved");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "payment_not_completed") {
        setError(detail);
      } else {
        setError(authRequestErrorMessage(e));
      }
      toast.error(authRequestErrorMessage(e));
    } finally {
      setCompleting(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (authLoading || autoStarted) return;
    if (!sessionId) {
      setError("Missing payment session");
      return;
    }
    setAutoStarted(true);
    confirmPayment();
  }, [sessionId, authLoading, autoStarted, confirmPayment]);

  if (!sessionId && !authLoading) {
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-20 text-center" data-testid="parking-complete-error">
        <h1 className="zc-headline text-3xl">Payment not confirmed</h1>
        <p className="text-[var(--zc-text-2)] mt-3">Missing payment session</p>
        <button type="button" onClick={() => navigate(`/parking/${id}`)} className="mt-8 px-5 h-11 zc-btn-primary text-sm">
          Return to spot
        </button>
      </div>
    );
  }

  if (authLoading || completing || (!error && !confirmed)) {
    return (
      <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20 text-[var(--zc-text-2)] text-center" data-testid="parking-complete-loading">
        Confirming your payment…
      </div>
    );
  }

  if (error) {
    const isPaymentIncomplete = error === "payment_not_completed";
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-20 text-center" data-testid="parking-complete-error">
        <h1 className="zc-headline text-3xl">Payment not confirmed</h1>
        <p className="text-[var(--zc-text-2)] mt-3">{error}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {!isPaymentIncomplete && (
            <button
              type="button"
              onClick={confirmPayment}
              disabled={completing}
              className="px-5 h-11 zc-btn-primary text-sm disabled:opacity-60"
              data-testid="parking-complete-retry-confirm"
            >
              Retry
            </button>
          )}
          <button type="button" onClick={() => navigate(`/parking/${id}`)} className="px-5 h-11 zc-btn-ghost text-sm">
            Return to spot
          </button>
        </div>
      </div>
    );
  }

  const snap = confirmed.spot_snapshot || {};

  return (
    <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20" data-testid="parking-complete-confirmed">
      <div className="zc-card p-6 sm:p-10 text-center">
        <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(22,163,74,0.12)" }}>
          <CheckCircle2 size={24} className="text-[var(--zc-success)]" />
        </div>
        <div className="eyebrow mt-6">Confirmed</div>
        <h1 className="zc-headline text-4xl mt-2">Parking reserved.</h1>
        <p className="text-[var(--zc-text-2)] mt-3 max-w-md mx-auto">
          {snap.name || "Parking spot"} · {confirmed.hours}h
        </p>
        {(confirmed.check_in || confirmed.check_out) && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto text-left">
            {confirmed.check_in && (
              <div className="zc-card p-4">
                <div className="eyebrow mb-1">Check-in</div>
                <div className="text-sm font-semibold text-[var(--zc-text)]">{formatTripDateLabel(confirmed.check_in)}</div>
                <div className="text-xs text-[var(--zc-text-2)] mt-0.5">{formatTripTimeLabel(confirmed.check_in)}</div>
              </div>
            )}
            {confirmed.check_out && (
              <div className="zc-card p-4">
                <div className="eyebrow mb-1">Check-out</div>
                <div className="text-sm font-semibold text-[var(--zc-text)]">{formatTripDateLabel(confirmed.check_out)}</div>
                <div className="text-xs text-[var(--zc-text-2)] mt-0.5">{formatTripTimeLabel(confirmed.check_out)}</div>
              </div>
            )}
          </div>
        )}
        {(snap.address || snap.city) && (
          <div className="mt-6 text-left max-w-sm mx-auto text-sm">
            <div className="flex gap-2 text-[var(--zc-text-2)]">
              <MapPin size={14} className="text-[var(--zc-accent)] shrink-0 mt-0.5" />
              <div>
                <div className="eyebrow">Location</div>
                <div className="text-[var(--zc-text)]">{[snap.address, snap.city].filter(Boolean).join(", ")}</div>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Booking</div>
            <div className="font-mono text-xs">{confirmed.booking_id}</div>
          </div>
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Total</div>
            <div className="zc-price text-2xl">${formatUsd(confirmed.total)}</div>
          </div>
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Payment</div>
            <div className="text-sm flex items-center gap-2 mt-1 justify-center">
              <span className="dot live" /> Stripe
            </div>
          </div>
        </div>
        <div className="flex justify-center gap-3 mt-10">
          <Link to="/profile/parking" className="px-5 h-11 grid place-items-center zc-btn-primary text-sm">My parking</Link>
          <Link to="/parking" className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm">Find more</Link>
        </div>
      </div>
    </div>
  );
}
